import { ref } from 'vue';
import contactsService from '@/services/contactsService';
import type { Contact } from '@/types';
import type { EmailAddress } from '@/types';

export function useContactsSearch() {
  const results = ref<Contact[]>([]);
  const suggestedContacts = ref<Contact[]>([]);
  const isSearching = ref(false);
  const highlightedIndex = ref(-1);
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let suggestedLoaded = false;

  function search(query: string) {
    if (debounceTimer) clearTimeout(debounceTimer);

    if (!query || query.trim().length < 1) {
      results.value = [];
      highlightedIndex.value = -1;
      return;
    }

    debounceTimer = setTimeout(async () => {
      isSearching.value = true;
      try {
        const response = await contactsService.searchContacts(query.trim(), 10);
        results.value = response?.contacts ?? [];
        highlightedIndex.value = results.value.length > 0 ? 0 : -1;
      } catch {
        results.value = [];
      } finally {
        isSearching.value = false;
      }
    }, 300);
  }

  async function loadSuggested() {
    if (suggestedLoaded) return;
    try {
      const response = await contactsService.getSuggestedContacts(5);
      suggestedContacts.value = response?.contacts ?? [];
      suggestedLoaded = true;
    } catch {
      suggestedContacts.value = [];
    }
  }

  function contactToEmailAddress(contact: Contact, emailIndex = 0): EmailAddress {
    const email = contact.emailAddresses[emailIndex]?.address ?? '';
    return {
      email,
      name: contact.displayName || undefined,
    };
  }

  function navigateDown(listLength: number) {
    highlightedIndex.value = Math.min(highlightedIndex.value + 1, listLength - 1);
  }

  function navigateUp() {
    highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
  }

  function resetSearch() {
    results.value = [];
    highlightedIndex.value = -1;
    isSearching.value = false;
    if (debounceTimer) clearTimeout(debounceTimer);
  }

  function cleanup() {
    if (debounceTimer) clearTimeout(debounceTimer);
  }

  return {
    results,
    suggestedContacts,
    isSearching,
    highlightedIndex,
    search,
    loadSuggested,
    contactToEmailAddress,
    navigateDown,
    navigateUp,
    resetSearch,
    cleanup,
  };
}
